import { UpdatePage } from '@components/molecules';
import { TextFieldElement, useForm } from 'react-hook-form-mui';
import API from '@apps/nnc/services/nnc';
import { ModelProps } from '@/types';
import { NncSchema, NncSchemaType } from '@apps/nnc/schemas/nnc';
import { JSX, useState, useCallback } from 'react';
import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';
// import DivisionAPI from '@apps/localization/services/division';
import ProductionPhaseAPI from '@apps/nnc/services/productionphase';
import MachineAPI from '@apps/localization/services/machine';
import EnrichmentAPI from '@apps/nnc/services/nncenrichment';
import { useAuth } from '@/contexts/auth';
import UnitAPI from '@apps/localization/services/unit';

export default function ObjectUpdatePage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();
  const formContext = useForm<NncSchemaType>();
  const refCode = formContext.watch('reference_number', undefined);
  const [editing, setEditing] = useState<NncSchemaType | null>(null);
  const { user } = useAuth();

  const handleDraft = () => {
    formContext.setValue('status', { id: 2, label: t('Draft') });
  };
  const handleSubmit = () => {
    formContext.setValue('status', { id: 3, label: t('Enriched') });
    formContext.setValue('modified_by', user?.id);
  };

  const checkDisabled =
    editing?.status?.id == 3 || editing?.status?.id == 4 || false;

  const getUnitDivisions = useCallback(async () => {
    try {
      const unitId = formContext.getValues('unit')?.id;
      if (!unitId) {
        return [];
      }
      console.log('Fetching divisions for unit:', unitId);
      const response = await UnitAPI.getUnitDivisions(unitId);
      return response;
    } catch (error) {
      console.error('Error fetching unit divisions:', error);
      return [];
    }
  }, [formContext]);

  const getProductionPhases = useCallback(
    () =>
      ProductionPhaseAPI.select({
        is_active: 1,
        unit: formContext.getValues('unit')?.id,
        division: formContext.getValues('division')?.id,
      }),
    [formContext],
  );

  const getMachines = useCallback(
    () =>
      MachineAPI.select({
        is_active: 1,
        unit: formContext.getValues('unit')?.id,
        production_phase: formContext.getValues('production_phase')?.id,
      }),
    [formContext],
  );

  const handleEditing = useCallback((data: NncSchemaType) => {
    setEditing(data);
  }, []);

  return (
    <UpdatePage
      schema={NncSchema}
      api={API}
      modelProps={modelProps}
      title={refCode !== undefined ? refCode : t('NNC Management')}
      formcontext={formContext}
      customPath={'nnc/management'}
      submitApi={EnrichmentAPI}
      disableUpdate
      enableDraft={editing?.status?.id !== 3 && editing?.status?.id !== 4}
      handleDraft={handleDraft}
      handleSubmitFunction={handleSubmit}
      hideSave={checkDisabled}
      onEditing={handleEditing}
    >
      <Grid size={{ xs: 12, md: 12 }}>
        <Typography
          fontWeight={'bold'}
          sx={{
            fontSize: 22,
          }}
        >
          SAP ZNCR
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextFieldElement
          disabled
          name="country"
          label={t('Country')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextFieldElement
          disabled
          name="unit.label"
          label={t('Unit')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextFieldElement
          disabled
          name="nnc_date"
          label={t('NNC Date')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextFieldElement
          disabled
          name="product"
          label={t('Product')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextFieldElement
          disabled
          name="operation"
          label={t('Operation')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextFieldElement
          disabled
          name="production_order"
          label={t('Production Order')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextFieldElement
          disabled
          name="operator_name"
          label={t('Operator Who Registered')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextFieldElement
          disabled
          name="machine"
          label={t('Machine')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextFieldElement
          disabled
          name="failure_category_description"
          label={t('Failure Category Description')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 12 }}>
        <TextFieldElement
          disabled
          name="description"
          label={t('Description')}
          fullWidth
          required
          multiline
          minRows={2}
        />
      </Grid>

      <Grid mt={3} size={{ xs: 12, md: 12 }}>
        <Typography
          fontWeight={'bold'}
          sx={{
            fontSize: 22,
          }}
        >
          {t('NNC Initial Details')}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <AsyncAutocompleteElement
          api={getUnitDivisions}
          name="division"
          label={t('Identified Area')}
          required
          autocompleteProps={{
            disabled: checkDisabled,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <AsyncAutocompleteElement
          api={() => getProductionPhases()}
          name="production_phase"
          label={t('Production Phase')}
          required
          autocompleteProps={{
            disabled: checkDisabled,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <AsyncAutocompleteElement
          api={() => getMachines()}
          name="machine_enrichment"
          label={t('NNC Machine')}
          required
          autocompleteProps={{
            disabled: checkDisabled,
          }}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 2 }}>
        <TextFieldElement
          disabled={checkDisabled}
          name="operator_code"
          label={t('Operator Cod.')}
          fullWidth
          required
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextFieldElement
          disabled={checkDisabled}
          name="operator_name_enrichment"
          label={t('Operator Generator')}
          fullWidth
          required
        />
      </Grid>
      <Grid mb={3} size={{ xs: 12, md: 12 }}>
        <TextFieldElement
          disabled={checkDisabled}
          name="comment"
          label={t('NNC Comment')}
          fullWidth
          required
          multiline
          minRows={2}
        />
      </Grid>
    </UpdatePage>
  );
}
